import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useIdentityAuth } from '@auth/IdentityAuthContext';
import {
  FeatureAccessLevel,
  OrganisationAccess,
  FlatStore,
} from '@/types/userAccess';

interface UserAccessContextState {
  fullName: string | null;
  hasSmpAccess: boolean;
  hasSmpOrgGrant: boolean;
  hasSmpEnabledOrg: boolean;
  organisations: OrganisationAccess[];
  allStores: FlatStore[];
  selectedStore: FlatStore | null;
  selectStore: (storeId: number) => void;
  hasAuditLogAccess: boolean;
  canViewProducts: boolean;
  canEditProducts: boolean;
  canViewChannels: boolean;
  canViewPause: boolean;
  canPauseChannels: boolean;
  canViewStoreHours: boolean;
  canEditStoreHours: boolean;
  canViewGeneralHours: boolean;
  canEditGeneralHours: boolean;
  isReadOnly: boolean;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

interface UserAccessProviderProps {
  children: ReactNode;
}

const UserAccessContext = createContext<UserAccessContextState | undefined>(
  undefined
);

const SELECTED_STORE_KEY = 'selectedStoreId';

function flattenStores(organisations: OrganisationAccess[]): FlatStore[] {
  const stores: FlatStore[] = [];
  for (const org of organisations) {
    if (!org.hasSmpAccess || org.smpEnabled === false) continue;
    for (const market of org.markets) {
      for (const store of market.stores) {
        stores.push({
          storeId: store.storeId,
          storeName: store.storeName,
          guestFacingName: store.guestFacingName,
          accessLevel: store.accessLevel,
          marketId: market.marketId,
          marketName: market.marketName,
          organisationId: org.organisationId,
          organisationName: org.organisationName,
        });
      }
    }
  }
  return stores;
}

function getPersistedStoreId(): number | null {
  if (typeof window !== 'undefined' && window.sessionStorage) {
    const stored = sessionStorage.getItem(SELECTED_STORE_KEY);
    return stored ? parseInt(stored, 10) : null;
  }
  return null;
}

function persistStoreId(storeId: number): void {
  if (typeof window !== 'undefined' && window.sessionStorage) {
    sessionStorage.setItem(SELECTED_STORE_KEY, storeId.toString());
  }
}

export const UserAccessProvider: React.FC<UserAccessProviderProps> = ({
  children,
}) => {
  const { userAccessData, refetchUserAccess } = useIdentityAuth();
  const [selectedStoreId, setSelectedStoreId] = useState<number | null>(
    getPersistedStoreId
  );

  const loading = !userAccessData;
  const error: string | null = null;

  const organisations = useMemo(
    () => userAccessData?.organisations ?? [],
    [userAccessData?.organisations]
  );

  const allStores = useMemo(
    () => flattenStores(organisations),
    [organisations]
  );

  const hasSmpOrgGrant = useMemo(
    () => organisations.some(org => org.hasSmpAccess),
    [organisations]
  );

  const hasSmpEnabledOrg = useMemo(
    () =>
      organisations.some(org => org.hasSmpAccess && org.smpEnabled !== false),
    [organisations]
  );

  const selectedStore = useMemo(() => {
    if (allStores.length === 0) return null;

    if (selectedStoreId !== null) {
      const found = allStores.find(s => s.storeId === selectedStoreId);
      if (found) return found;
    }

    return allStores[0];
  }, [allStores, selectedStoreId]);

  const selectStore = useCallback((storeId: number) => {
    setSelectedStoreId(storeId);
    persistStoreId(storeId);
  }, []);

  const currentOrgAccess = useMemo(() => {
    if (!selectedStore || !organisations.length) return null;
    return (
      organisations.find(
        org => org.organisationId === selectedStore.organisationId
      ) ?? null
    );
  }, [selectedStore, organisations]);

  const hasAuditLogAccess = currentOrgAccess?.hasAuditLogAccess ?? false;

  // An absent level means the backend predates these permissions, so the store access
  // level alone decides — that keeps SMP usable while CMS is still rolling out.
  const canView = (level?: FeatureAccessLevel) =>
    level === undefined || level !== 'HIDDEN';
  const canEdit = (level?: FeatureAccessLevel) =>
    level === undefined || level === 'EDIT';

  const canViewProducts = canView(currentOrgAccess?.productsAccess);
  const canEditProducts = canEdit(currentOrgAccess?.productsAccess);
  const canViewPause = canView(currentOrgAccess?.channelPauseAccess);
  const canPauseChannels = canEdit(currentOrgAccess?.channelPauseAccess);
  const canViewStoreHours = canView(currentOrgAccess?.storeHoursAccess);
  const canEditStoreHours = canEdit(currentOrgAccess?.storeHoursAccess);
  // General hours share the Store Hours permission with the channel hours.
  const canViewGeneralHours = canViewStoreHours;
  const canEditGeneralHours = canEditStoreHours;
  const canViewChannels = canViewPause || canViewStoreHours;

  const isReadOnly = useMemo(() => {
    if (!currentOrgAccess?.hasSmpAccess) return true;
    if (!selectedStore) return true;
    return selectedStore.accessLevel !== 'EDIT';
  }, [currentOrgAccess, selectedStore]);

  const refetch = useCallback(() => {
    refetchUserAccess();
  }, [refetchUserAccess]);

  const contextValue: UserAccessContextState = {
    fullName: userAccessData?.fullName ?? null,
    hasSmpAccess: userAccessData?.hasSmpAccess ?? false,
    hasSmpOrgGrant,
    hasSmpEnabledOrg,
    organisations,
    allStores,
    selectedStore,
    selectStore,
    hasAuditLogAccess,
    canViewProducts,
    canEditProducts,
    canViewChannels,
    canViewPause,
    canPauseChannels,
    canViewStoreHours,
    canEditStoreHours,
    canViewGeneralHours,
    canEditGeneralHours,
    isReadOnly,
    loading,
    error,
    refetch,
  };

  return (
    <UserAccessContext.Provider value={contextValue}>
      {children}
    </UserAccessContext.Provider>
  );
};

export const useUserAccess = (): UserAccessContextState => {
  const context = useContext(UserAccessContext);
  if (context === undefined) {
    throw new Error('useUserAccess must be used within a UserAccessProvider');
  }
  return context;
};

export { UserAccessContext };
